import { useEffect, useState } from 'react'

/**
 * Subscribe to a media query.
 *
 * Used for the one layout decision that cannot be made in CSS: on a phone the
 * sidebar is an **overlay drawer** with a backdrop and focus handling, and on a
 * desktop it is a **persistent column**. Those are different components with
 * different behaviour, not one component at two widths.
 *
 * The initial read happens in the state initialiser rather than in an effect,
 * so the first paint is already correct and a phone never flashes a desktop
 * layout.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    if (!window.matchMedia) return
    const list = window.matchMedia(query)
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches)
    // Keep in step if the query itself changes, and on rotate/resize.
    setMatches(list.matches)
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query])

  return matches
}

/** The one breakpoint this app has. Below it, the sidebar becomes a drawer. */
export const MOBILE = '(max-width: 767px)'
